import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import { BoxShadow } from '../../../shared/GlobalStyles';

const Wrapper = styled('div')`
  position: relative;
  display: inline-flex;
`;

const Trigger = styled('button')`
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: 600;
  border: none;
  border-radius: 8px;
  padding: 0 14px;
  height: ${(props) => props.triggerHeight || 36}px;
  cursor: pointer;
  white-space: nowrap;
  box-shadow: ${BoxShadow};
  background: ${(props) => (props.hasSelection ? '#1a3a2a' : 'white')};
  color: ${(props) => (props.hasSelection ? 'white' : '#444')};
  display: flex;
  align-items: center;
  transition: background 0.15s ease, color 0.15s ease;

  &:hover {
    opacity: 0.88;
  }
`;

const Panel = styled('div')`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  background: white;
  box-shadow: ${BoxShadow};
  border-radius: 8px;
  z-index: 200;
  min-width: 220px;
  display: flex;
  flex-direction: column;
  max-height: 320px;
`;

const GroupList = styled('div')`
  flex: 1;
  overflow-y: auto;
  padding: 6px 0;
`;

const GroupItem = styled('label')`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 16px;
  cursor: pointer;
  font-family: 'Nunito Sans';
  font-size: 13px;
  color: #444;

  &:hover {
    background: #f5f5f5;
  }

  input[type='checkbox'] {
    width: 14px;
    height: 14px;
    cursor: pointer;
    flex-shrink: 0;
  }
`;

const SearchBox = styled('div')`
  padding: 8px 10px;
  border-bottom: 1px solid #eee;
  flex-shrink: 0;
`;

const SearchInput = styled('input')`
  width: 100%;
  font-family: 'Nunito Sans';
  font-size: 13px;
  color: #444;
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 5px 10px;
  outline: none;
  box-sizing: border-box;

  &:focus {
    border-color: #aaa;
  }
`;

const EmptyMsg = styled('div')`
  padding: 12px 16px;
  font-family: 'Nunito Sans';
  font-size: 13px;
  color: #999;
`;

const PanelFooter = styled('div')`
  display: flex;
  gap: 8px;
  padding: 10px 12px;
  border-top: 1px solid #eee;
  flex-shrink: 0;
`;

const FooterBtn = styled('button')`
  flex: 1;
  height: 32px;
  border-radius: 6px;
  font-family: 'Nunito Sans';
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: ${(props) => (props.primary ? 'none' : '1px solid #ddd')};
  background: ${(props) => (props.primary ? '#1a3a2a' : 'white')};
  color: ${(props) => (props.primary ? 'white' : '#444')};

  &:hover {
    opacity: 0.85;
  }
`;

const getTriggerLabel = (selectedGroups) => {
  if (selectedGroups.length === 0) return 'Filter by Groups';
  if (selectedGroups.length === 1) return '1 Group';
  return `${selectedGroups.length} Groups`;
};

const GroupFilterDropdown = ({
  groups, selectedGroups, onChange, height,
}) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(selectedGroups);
  const [search, setSearch] = useState('');
  const wrapperRef = useRef(null);

  const filtered = groups.filter(
    (g) => g.name.toLowerCase().includes(search.toLowerCase().trim()),
  );

  useEffect(() => {
    if (open) {
      setDraft(selectedGroups);
      setSearch('');
    }
  }, [open]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggle = (group) => {
    const already = draft.some((g) => g.id === group.id);
    setDraft(already ? draft.filter((g) => g.id !== group.id) : [...draft, group]);
  };

  const handleSave = () => {
    onChange(draft);
    setOpen(false);
  };

  const handleClear = () => {
    setDraft([]);
    onChange([]);
    setOpen(false);
  };

  return (
    <Wrapper ref={wrapperRef}>
      <Trigger
        type='button'
        hasSelection={selectedGroups.length > 0}
        triggerHeight={height}
        onClick={() => setOpen((prev) => !prev)}
      >
        {getTriggerLabel(selectedGroups)}
      </Trigger>

      {open && (
        <Panel>
          <SearchBox>
            <SearchInput
              type='text'
              placeholder='Search groups...'
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />
          </SearchBox>
          <GroupList>
            {filtered.length === 0 ? (
              <EmptyMsg>No groups found.</EmptyMsg>
            ) : (
              filtered.map((group) => (
                <GroupItem key={group.id}>
                  <input
                    type='checkbox'
                    checked={draft.some((g) => g.id === group.id)}
                    onChange={() => toggle(group)}
                  />
                  {group.name}
                </GroupItem>
              ))
            )}
          </GroupList>
          <PanelFooter>
            <FooterBtn type='button' onClick={handleClear}>Clear</FooterBtn>
            <FooterBtn type='button' primary onClick={handleSave}>Save</FooterBtn>
          </PanelFooter>
        </Panel>
      )}
    </Wrapper>
  );
};

GroupFilterDropdown.defaultProps = {
  height: 36,
};

GroupFilterDropdown.propTypes = {
  groups: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
  })).isRequired,
  selectedGroups: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  onChange: PropTypes.func.isRequired,
  height: PropTypes.number,
};

export default GroupFilterDropdown;
